import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { PromptContext } from './PromptContext';
import { UserContext } from './UserAndProfile/UserContext';
// LEGACY ROLLBACK: import { SpeechToTextComponent } from './SpeechToText';
import { OpenAIVoiceRecorder } from './Voice/OpenAIVoiceRecorder';
import { Box, Button, Typography, TextField, useTheme, IconButton, Fade, keyframes } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { motion, AnimatePresence } from 'framer-motion';
import { getStrategyCard, isStrategyCardPrompt } from '../data/skillStrategyCards';
import StrategyCard from './Chat/StrategyCard';
import GenerationStatusLine from './Chat/GenerationStatusLine';
import ExpandableAssistantMessage from './Chat/ExpandableAssistantMessage';
import StudyPlanNudge from './Chat/StudyPlanNudge';
import {
  ChatMessage,
  GenerationPhase,
  createMessageId,
  buildPersonalizePrompt,
  buildExpandPrompt,
  evaluateStudyPlanNudge,
  clearNudgeDismiss,
  countWords,
} from './Chat/chatUtils';

const isDev = process.env.NODE_ENV === 'development';

const typingBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

type ChatComponentProps = {
  isMissedQuestionReview?: boolean;
};

const TypingIndicator: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 7, height: 7, borderRadius: '50%',
          bgcolor: 'text.secondary',
          animation: `${typingBounce} 1.2s ease-in-out infinite`,
          animationDelay: `${i * 0.15}s`,
        }}
      />
    ))}
  </Box>
);

const ChatComponent: React.FC<ChatComponentProps> = ({ isMissedQuestionReview = false }) => {
  const [userInput, setUserInput] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [conversation, setConversation] = useState<ChatMessage[]>([]);
  const [selectedModel] = useState<string>('gpt-5.4-mini');
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isGeneratingResponse, setIsGeneratingResponse] = useState<boolean>(false);
  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>('idle');
  const [expandingMessageId, setExpandingMessageId] = useState<string | null>(null);
  const [showCompletionNudge, setShowCompletionNudge] = useState(false);
  const [strategyPreviewOpen, setStrategyPreviewOpen] = useState(false);

  const { user } = useContext(UserContext);
  const context = useContext(PromptContext);
  const generatedQuestion = context?.questionText || '';
  const generatedQuestionChoices = context?.choices || '';
  const generatedQuestionAnswer = context?.correctAnswer || '';
  const generatedQuestionExplanation = context?.explanationText || '';
  const selectedSkill = context?.selectedSkill || '';

  const skillsRequiringLatex = [
    'Absolute Value', 'Algebra', 'Circles', 'Exponential Equations', 'Exponential Word Problems',
    'Geometry', 'Interpreting Graphs', 'Linear Equations', 'Linear Word Problems',
    'Inequality Word Problems', 'Percent', 'Polynomial Expressions', 'Probability',
    'Quadratic Equations', 'Quadratic Word Problems', 'Statistics', 'Systems of Equations',
    'Trigonometry', 'Unit Conversions',
  ];
  const skillsRequiringHTMLTables = ['Interpreting Graphs', 'Referencing Data'];
  const containsMath = skillsRequiringLatex.includes(selectedSkill);
  const requiresHTMLTables = skillsRequiringHTMLTables.includes(selectedSkill);

  const theme = useTheme();

  const systemPrompt = useMemo(
    () => `You are Kai, an AI tutor specialized in helping high school students learn relevant core knowledge and test-taking strategies to solve Digital SAT problems. 
    Your responses should be easily understandable, short (50-80 words by default), supportive, and engaging for high school students. Very directly and succinctly share test-taking strategies relevant to the question, then ask the user questions to help develop their knowledge along the way.
    Generally, show how to implement a relevant strategy and 1-2 steps forward in solving the problem but don't explicitly give the answer right away. If the student asks for the answer, definitely give it. Don't ask more than 2 questions back to back unless the student's understanding requires further support or scaffolding.
    Default to 50–80 words. End with at most one follow-up question. The student can tap Expand for more detail—do not preemptively write a long explanation unless they ask.
    
    ${
      user
        ? `About Me, the high-school Student User: My name is ${user.firstName}. I enjoy encouraging and insightful feedback. 
        My goal score for the SAT is ${user.targetScores.readingWriting} for Reading and Writing and ${user.targetScores.math} for Math. Consider this when asked 'Which question types should I answer first'. Also, Word Choice, Punctuation, Linear Word Problems, and Quadratic Equations are the 4 most common question types.`
        : `I enjoy encouraging and insightful feedback. When asked 'Which question types should I answer first', recommend one of the 4 most common question types: Word Choice, Punctuation, Linear Word Problems, and Quadratic Equations, but also ask what the student is most interested in or where they enjoy practicing - because enthusiasm and motivation are important to leverage and celebrate!`
    } 
    
    ${
      containsMath
        ? 'Formatting: For all mathematical expressions, YOU MUST USE LaTeX formatting with "$" before and after the math expression. If a question should show a "$" to show the cost, you MUST DOUBLE ESCAPE "\\\\"IT: "\\\\$". Model your math process step by step on different lines with double line breaks in between. FOr each step, show the math expression first, then have 5 spaces ("     "), then include a concise explanation of what is happening in the step. Example: $y = \\frac{1}{2}x + 6$     First, we copy the equation down.\n\n$10 = \\frac{1}{2}x + 6$     Second, we plug in information given by the question, substituting $10$ for $y$.\n\n$4 = \\frac{1}{2}x$     Third, we subtract $6$ from both sides as we try to isolate $x$.\n\n$8 = x$     Finally, we multiplied both sides by $2$ to eliminate the fraction, leaving us with $8 = x$.'
        : ''
    }
    ${
      requiresHTMLTables
        ? 'Formatting: If you need to reference the table in any future chat, make sure to use HTML formatting. Example: <table border=\\"1\\"><tr><th>Group</th><th>Number of cans</th></tr><tr><td>1</td><td>30</td></tr><tr><td>2</td><td>63</td></tr><tr><td>3</td><td>38</td></tr><tr><td>4</td><td>50</td></tr><tr><td>5</td><td>47</td></tr><tr><td>6</td><td>40</td></tr><tr><td>7</td><td>54</td></tr><tr><td>8</td><td>60</td></tr><tr><td>9</td><td>18</td></tr><tr><td>10</td><td>20</td></tr></table>'
        : ''
    }
    `,
    [user, containsMath, requiresHTMLTables]
  );

  // Clear chat when PromptContext signals a new question
  useEffect(() => {
    try {
      if (context?.chatCleared && conversation.length > 0) {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setIsGeneratingResponse(false);
        setGenerationPhase('idle');
        setConversation([]);
        setShowCompletionNudge(false);
        setStrategyPreviewOpen(false);
        clearNudgeDismiss();
      }
    } catch (err) {
      if (isDev) console.log('Error clearing Conversation: ', err);
    }
  }, [context?.chatCleared]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Progressive status phases while waiting for first token
  useEffect(() => {
    if (!isGeneratingResponse) {
      setGenerationPhase('idle');
      return;
    }

    setGenerationPhase('reading');
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setGenerationPhase((p) => (p === 'streaming' ? p : generatedQuestion ? 'context' : 'drafting')), 800));
    timers.push(setTimeout(() => setGenerationPhase((p) => (p === 'streaming' ? p : 'drafting')), 1600));

    return () => timers.forEach(clearTimeout);
  }, [isGeneratingResponse, generatedQuestion]);

  let quickPrompts: string[] = [];
  if (isMissedQuestionReview) {
    quickPrompts = [
      'What did I do wrong?',
      'What should I do differently next time?',
      "What's a test-taking strategy I could use here?",
      'Can you teach me the core knowledge / rule for this question type?',
    ];
  } else {
    quickPrompts =
      context?.interactionStage === 'notStarted'
        ? [
            'Which question types should I practice first?',
            'What are common study mistakes?',
            'Can you tell me a joke and then inspire me to work hard?',
          ]
        : context?.interactionStage === 'questionGenerated'
          ? ['Can you give me a hint to start?', "What's the key knowledge / strategy I should know?"]
          : ["I don't get this. Can you explain how to solve this?", 'What strategy could I have used?'];
  }

  const appendStrategyCard = (skill: string, userPrompt: string) => {
    const card = getStrategyCard(skill);
    const userMsg: ChatMessage = { id: createMessageId(), role: 'user', content: userPrompt };
    const assistantMsg: ChatMessage = {
      id: createMessageId(),
      role: 'assistant',
      content: '',
      source: 'strategy-card',
      strategySkill: skill,
      canExpand: false,
    };
    setConversation((prev) => [...prev, userMsg, assistantMsg]);
    setUserInput('');
    setStrategyPreviewOpen(false);
  };

  const streamChat = async (
    messagesForAPI: { role: string; content: string }[],
    assistantMessageId: string,
    options: {
      appendToExisting?: boolean;
      markExpandable?: boolean;
      existingContent?: string;
    } = {}
  ) => {
    const { appendToExisting = false, markExpandable = true, existingContent = '' } = options;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsGeneratingResponse(true);
    setError('');

    const startTime = performance.now();
    const fetchStartTime = performance.now();
    let accumulatedContent = appendToExisting ? existingContent : '';
    let startedAppending = !appendToExisting;

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: messagesForAPI, model: selectedModel }),
        signal: abortController.signal,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Network error' }));
        setError(errorData.error || 'Something went wrong. Please try again.');
        setIsGeneratingResponse(false);
        setGenerationPhase('idle');
        setExpandingMessageId(null);
        return;
      }

      if (!appendToExisting) {
        const initialAssistantMessage: ChatMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          source: 'api',
        };
        setConversation((prev) => [...prev, initialAssistantMessage]);
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let firstTokenTime: number | null = null;

      if (!reader) throw new Error('No reader available');

      const finalizeMessage = (content: string) => {
        setConversation((prev) =>
          prev.map((m) => {
            if (m.id !== assistantMessageId) return m;
            const finalContent = content || m.content;
            return {
              ...m,
              content: finalContent,
              source: 'api' as const,
              canExpand: markExpandable && !appendToExisting && countWords(finalContent) < 120,
              isExpanded: appendToExisting ? true : m.isExpanded,
            };
          })
        );
        setIsGeneratingResponse(false);
        setGenerationPhase('idle');
        setExpandingMessageId(null);
        abortControllerRef.current = null;
        setShowCompletionNudge(true);
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);

          if (data === '[DONE]') {
            if (isDev) {
              const totalTime = performance.now() - startTime;
              console.log(`[Performance] Streaming completed in: ${totalTime.toFixed(0)}ms`);
            }
            finalizeMessage(accumulatedContent);
            return;
          }

          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              setError(`Server error: ${parsed.error}`);
              setIsGeneratingResponse(false);
              setGenerationPhase('idle');
              setExpandingMessageId(null);
              abortControllerRef.current = null;
              return;
            }

            if (parsed.content) {
              if (firstTokenTime === null) {
                firstTokenTime = performance.now();
                setGenerationPhase('streaming');
                if (isDev) {
                  console.log(`[Performance] Time to first token: ${(firstTokenTime - fetchStartTime).toFixed(0)}ms`);
                }
              }

              if (appendToExisting && !startedAppending) {
                accumulatedContent = existingContent + (existingContent ? '\n\n' : '') + parsed.content;
                startedAppending = true;
              } else {
                accumulatedContent += parsed.content;
              }

              setConversation((prev) =>
                prev.map((m) =>
                  m.id === assistantMessageId ? { ...m, content: accumulatedContent, source: 'api' as const } : m
                )
              );
            }
          } catch {
            continue;
          }
        }
      }

      finalizeMessage(accumulatedContent);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setIsGeneratingResponse(false);
        setGenerationPhase('idle');
        setExpandingMessageId(null);
        abortControllerRef.current = null;
        return;
      }
      if (isDev) console.error('fetchChatResponse error: ', err);
      setError('Failed to fetch data from the server.');
      setIsGeneratingResponse(false);
      setGenerationPhase('idle');
      setExpandingMessageId(null);
      abortControllerRef.current = null;
    }
  };

  const getTruncatedConversation = (fullConversation: ChatMessage[], maxMessages: number = 10): ChatMessage[] => {
    const nonSystemMessages = fullConversation.filter((msg) => msg.role !== 'system');
    const truncatedMessages = nonSystemMessages.slice(-maxMessages);
    return [{ id: 'system', role: 'system', content: systemPrompt }, ...truncatedMessages];
  };

  const fetchChatResponse = async (input: string = userInput): Promise<void> => {
    if (!input.trim() || isGeneratingResponse) return;

    // Instant strategy card — no API
    if (isStrategyCardPrompt(input) && selectedSkill) {
      appendStrategyCard(selectedSkill, input);
      return;
    }

    const userMessageContentForAPI =
      input +
      (generatedQuestion
        ? `Answer the question directly, with a positive and fun tone by applying and modeling a relevant strategy immediately in the first sentence. Don't restate the question or task because I want to receive feedback and learn within the first 1-2 sentences. You should use the context of the current SAT question to answer the question:
            Current SAT Question: ${generatedQuestion}. 
            Answer Choices: ${generatedQuestionChoices}. 
            Correct Answer: ${generatedQuestionAnswer}. 
            Official SAT Explanation: ${generatedQuestionExplanation}.`
        : '');

    const newUserMessageForAPI: ChatMessage = { id: createMessageId(), role: 'user', content: userMessageContentForAPI };
    const updatedConversationForAPI = [...conversation, newUserMessageForAPI];
    const messagesForAPI = getTruncatedConversation(updatedConversationForAPI).map(({ role, content }) => ({ role, content }));

    const newUserMessageForDisplay: ChatMessage = { id: createMessageId(), role: 'user', content: input };
    const assistantMessageId = createMessageId();

    setConversation((prev) => [...prev, newUserMessageForDisplay]);
    setUserInput('');
    setStrategyPreviewOpen(false);

    await streamChat(messagesForAPI, assistantMessageId);
  };

  const handlePersonalizeStrategy = (skill: string) => {
    if (isGeneratingResponse) return;
    const card = getStrategyCard(skill);
    const prompt = buildPersonalizePrompt(card);
    fetchChatResponse(prompt);
  };

  const expandAssistantMessage = async (messageId: string) => {
    if (isGeneratingResponse || expandingMessageId) return;

    const existing = conversation.find((m) => m.id === messageId);
    if (!existing) return;

    setExpandingMessageId(messageId);

    const expandUserContent =
      buildExpandPrompt() +
      (generatedQuestion
        ? `\nCurrent SAT Question: ${generatedQuestion}. Answer Choices: ${generatedQuestionChoices}. Correct Answer: ${generatedQuestionAnswer}.`
        : '');

    const historyForAPI = getTruncatedConversation(conversation).map(({ role, content }) => ({ role, content }));
    historyForAPI.push({ role: 'user', content: expandUserContent });

    await streamChat(historyForAPI, messageId, {
      appendToExisting: true,
      markExpandable: false,
      existingContent: existing.content,
    });
  };

  const handleQuickPromptClick = (quickPrompt: string) => {
    if (isGeneratingResponse) return;
    setUserInput(quickPrompt);
    setTimeout(() => {
      fetchChatResponse(quickPrompt);
    }, 0);
  };

  const handleSpeechToText = (text: string) => {
    setUserInput((prevInput) => (prevInput ? `${prevInput} ${text}` : text));
    if (isDev) console.log(`Speech transcript: ${text}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(e.target.value);
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const maxHeight = 6 * 24;
      const nextHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
      textareaRef.current.style.height = `${nextHeight}px`;
      textareaRef.current.style.overflowY =
        textareaRef.current.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, [userInput]);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom && conversation.length > 0);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const stopGenerating = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsGeneratingResponse(false);
    setGenerationPhase('idle');
    setExpandingMessageId(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isGeneratingResponse) return;
    fetchChatResponse();
  };

  const lastMessage = conversation[conversation.length - 1];
  const showTypingIndicator =
    isGeneratingResponse &&
    !expandingMessageId &&
    (conversation.length === 0 ||
      lastMessage?.role === 'user' ||
      (lastMessage?.role === 'assistant' && !lastMessage.content && lastMessage.source !== 'strategy-card'));

  const userMessageCount = conversation.filter((m) => m.role === 'user').length;
  const completionNudge =
    showCompletionNudge && !isGeneratingResponse
      ? evaluateStudyPlanNudge(user, selectedSkill, {
          isMissedQuestionReview,
          userMessageCount,
        })
      : null;

  const strategyPreviewCard =
    conversation.length === 0 &&
    context?.interactionStage === 'questionGenerated' &&
    selectedSkill
      ? getStrategyCard(selectedSkill)
      : null;

  const renderLatex = (content: string) =>
    context?.renderLatexOrText(content, 'small', true) ?? content;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden', p: { xs: 0, md: 1 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 2, flexShrink: 0 }}>
        {quickPrompts.map((quickPrompt, index) => (
          <Button
            key={index}
            variant="outlined"
            disabled={isGeneratingResponse}
            onClick={() => handleQuickPromptClick(quickPrompt)}
            sx={{
              textTransform: 'none', border: '2px solid', borderColor: 'primary.main',
              transform: 'scale 1.01', transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.01)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                bgcolor: 'action.hover',
              },
            }}
          >
            <Typography variant="body1">{quickPrompt}</Typography>
          </Button>
        ))}
      </Box>

      <StudyPlanNudge variant="mini-bar" />

      <Box sx={{ flexShrink: 0 }}>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px' }}
        >
          {/* LEGACY ROLLBACK: {isMobile ? null : <SpeechToTextComponent onTranscriptionUpdate={handleSpeechToText} />} */}
          <OpenAIVoiceRecorder
            onTranscriptionUpdate={handleSpeechToText}
            isDisabled={isGeneratingResponse}
          />

          <TextField
            inputRef={textareaRef}
            value={userInput}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!isGeneratingResponse) fetchChatResponse();
              }
            }}
            placeholder="Tap the mic to talk or type your questions here! Ex: Can you explain ___?"
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            variant="outlined"
            sx={{
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                border: '2px solid', borderColor: 'divider', transition: 'all 0.2s ease-in-out',
                '& fieldset': { border: 'none' },
                '&:hover': { borderColor: 'primary.main' },
                '&.Mui-focused': { borderColor: 'primary.main' },
              },
            }}
          />
          {isGeneratingResponse ? (
            <Button type="button" variant="outlined" color="inherit" onClick={stopGenerating} sx={{ minWidth: '60px' }}>
              Stop
            </Button>
          ) : (
            <Button type="submit" variant="contained" sx={{ minWidth: '60px' }}>
              Send
            </Button>
          )}
        </form>
      </Box>

      {completionNudge && (
        <StudyPlanNudge
          variant="completion"
          nudge={completionNudge}
          onDismissed={() => setShowCompletionNudge(false)}
        />
      )}

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          ref={chatContainerRef}
          onScroll={handleScroll}
          sx={{
            flex: 1, overflowY: 'auto',
            minHeight: { xs: 250, sm: 300 },
            maxHeight: { xs: '45vh', sm: '55vh', md: '60vh' },
            px: { xs: 1, md: 2 }, py: 2,
            backgroundColor: 'background.default',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: 'grey.300', borderRadius: '3px',
              '&:hover': { background: 'grey.400' },
            },
          }}
        >
          {conversation.length === 0 && !isGeneratingResponse ? (
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100%', minHeight: 180, px: 2, textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 40, height: 40, mb: 1.5,
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                  K
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, mb: 0.5 }}>
                Ask Kai anything about this question
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: strategyPreviewCard ? 2 : 0 }}>
                Try a suggested question above, or type your own.
              </Typography>

              {strategyPreviewCard && (
                <Box sx={{ width: '100%', maxWidth: 480, textAlign: 'left', mt: 1 }}>
                  {!strategyPreviewOpen ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setStrategyPreviewOpen(true)}
                      sx={{ textTransform: 'none', borderColor: 'secondary.main', color: 'secondary.main' }}
                    >
                      See strategy for {strategyPreviewCard.skill}
                    </Button>
                  ) : (
                    <StrategyCard
                      card={strategyPreviewCard}
                      compact
                      personalizeDisabled={isGeneratingResponse}
                      onPersonalize={() => handlePersonalizeStrategy(strategyPreviewCard.skill)}
                    />
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <AnimatePresence initial={false}>
              {conversation.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  {msg.role === 'user' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                      <Box
                        sx={{
                          maxWidth: '70%',
                          backgroundColor: theme.palette.secondary.main,
                          px: 3, py: 2,
                          borderRadius: '18px',
                          borderBottomRightRadius: '4px',
                          wordBreak: 'break-word',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ lineHeight: 1.4, '& p': { margin: 0, color: 'white' } }}
                        >
                          {renderLatex(msg.content)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : msg.source === 'strategy-card' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 4 }}>
                      <Box sx={{ width: '100%', maxWidth: 520 }}>
                        <StrategyCard
                          card={getStrategyCard(msg.strategySkill || selectedSkill)}
                          personalizeDisabled={isGeneratingResponse}
                          onPersonalize={() =>
                            handlePersonalizeStrategy(msg.strategySkill || selectedSkill)
                          }
                        />
                      </Box>
                    </Box>
                  ) : (
                    <ExpandableAssistantMessage
                      message={msg}
                      renderContent={renderLatex}
                      onExpand={expandAssistantMessage}
                      isExpanding={expandingMessageId === msg.id}
                      readAloudDisabled={
                        isGeneratingResponse ||
                        expandingMessageId === msg.id ||
                        !msg.content.trim()
                      }
                      showTyping={!msg.content && isGeneratingResponse}
                      typingIndicator={
                        <Box>
                          <TypingIndicator />
                          <GenerationStatusLine
                            phase={generationPhase}
                            visible={isGeneratingResponse && generationPhase !== 'streaming'}
                          />
                        </Box>
                      }
                    />
                  )}
                </motion.div>
              ))}

              {showTypingIndicator && lastMessage?.role === 'user' && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 32, height: 32,
                        backgroundColor: theme.palette.secondary.main,
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, mt: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                        K
                      </Typography>
                    </Box>
                    <Box>
                      <TypingIndicator />
                      <GenerationStatusLine
                        phase={generationPhase}
                        visible={isGeneratingResponse && generationPhase !== 'streaming'}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </AnimatePresence>
          )}
        </Box>

        <Fade in={showScrollButton}>
          <IconButton
            onClick={scrollToBottom}
            sx={{
              position: 'absolute', bottom: 16, right: 16,
              bgcolor: 'primary.main', color: 'white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 2,
              '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.1)' },
              transition: 'all 0.2s ease',
            }}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Fade>
      </Box>

      {error && (
        <Typography variant="body2" sx={{ color: 'error.main', mt: 2, flexShrink: 0 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ChatComponent;
